import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { signIn } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

interface LoginFormValues {
  email: string
  password: string
}

/**
 * `POST /api/auth/sign-in` (task 6/030 design §4). The entry point to
 * everything else: a provisioned user has no session until they come through
 * here or through `SetPasswordRoute`.
 *
 * Validation is react-hook-form's `rules`, not the native HTML `required`
 * attribute — the same pattern `StyleguideRoute`'s `FormSection` already
 * establishes. An empty field never reaches `onSubmit`: `handleSubmit` runs
 * the field rules first and, on failure, renders `FormMessage` instead of
 * calling this component's submit handler at all.
 *
 * On a rejected sign-in, `body.error` — the server's OWN wording — is
 * rendered VERBATIM, never flattened to a generic string. The server
 * deliberately distinguishes a wrong credential (`'invalid email or
 * password'`) from a rate limit (`'too many attempts — try again shortly'`)
 * from a malformed body (zod's own message); collapsing all three to "sign
 * in failed" would throw that distinction away at the one place a user could
 * still read it.
 *
 * On success: invalidates `['session', 'me']` — the exact key `useSession`
 * reads — BEFORE navigating home, so whatever next reads that key (today,
 * nothing; from Task 11, the shell) gets a fresh `GET /api/me` rather than
 * whatever was cached (or absent) before sign-in. `session.tsx`'s own doc
 * comment explains why this is required, not optional, given `staleTime:
 * Infinity`. Verified directly (not merely reasoned about) via a throwaway
 * probe route during development — see this task's report for the transcript.
 *
 * THAT PARAGRAPH USED TO END "there is no committed way to observe it: the
 * only consumer of `useSession()` today is this invalidation call itself".
 * Both halves have since stopped being true, and the correction is kept
 * rather than the sentence quietly deleted, because the second half is what
 * made the invalidation look unobservable and so untestable — which is why
 * the defect fixed below (see `onSubmit`) survived to a real phone.
 * `shell/AppShell.tsx` reads `useSession()` and is the whole reason this
 * invalidation is load-bearing, and `test/auth.test.tsx`'s "lands in the app
 * after a sign-in once the boot `/api/me` has already answered 401" observes
 * it end to end WITHOUT touching the query cache — through the DOM, which is
 * what `docs/frontend-testing.md` rule 4 asks for rather than forbids.
 *
 * `navigate('/')` runs AFTER the invalidation is awaited, deliberately —
 * during development verification, imperative `navigate()` was found to
 * THROW inside `renderApp`'s harness. CORRECTED after review round 1: the
 * first write of this comment blamed `@mswjs/interceptors`, but a bare
 * repro with NO msw import in the process at all (`new Request(url, {
 * signal: new AbortController().signal })`, nothing else) throws the
 * identical error — msw cannot be the cause of something that reproduces
 * with msw absent. The real mechanism: vitest's `jsdom` environment replaces
 * `globalThis.AbortController`/`AbortSignal` with jsdom's own classes, while
 * `Request`/`fetch` stay Node's built-in, `undici`-backed implementations
 * (jsdom implements neither) — and undici's `Request` constructor brand-
 * checks an incoming `signal` against ITS OWN internal `AbortSignal` class,
 * not the global one. jsdom's `AbortSignal` fails that check even though
 * `signal instanceof AbortSignal` (the GLOBAL, jsdom-provided class) is
 * `true` — confirmed directly: `new AbortController().signal instanceof
 * AbortSignal` is `true` in this environment, and `new Request(url, {
 * signal })` still throws `"RequestInit: Expected signal ('AbortSignal {}')
 * to be an instance of AbortSignal."`. `@mswjs/interceptors` is a SECOND
 * victim of this same mismatch (it re-constructs a `Request` internally to
 * record raw headers), not the cause — react-router's OWN
 * `createClientSideRequest` hits it first, independent of MSW. A real
 * browser has exactly one `AbortSignal` realm and cannot hit this; the
 * error surfaces as an unhandled rejection from react-router's internal
 * promise, never reaching this function's `catch`. Recorded here because it
 * means no committed test in this task's suite exercises a successful
 * sign-in through to navigation — see the task-10 report for the full
 * transcript. A real fix (subclassing the global `Request` to mirror a
 * foreign signal onto a Node `AbortController`, while KEEPING jsdom's own
 * `AbortController` so Radix/RTL's `addEventListener(..., { signal })` still
 * works — wholesale-swapping the globals for Node's was measured to break
 * that) belongs in a CUSTOM VITEST ENVIRONMENT, not `test/setup.ts` (setup
 * files run AFTER the environment has already installed its globals, by
 * which point jsdom's classes are what every already-imported module holds
 * a reference to — there is no recovering Node's originals from there).
 * Deliberately NOT implemented by this task: Task 11 is the first task with
 * real navigation assertions to need it.
 */
export function LoginRoute() {
  const form = useForm<LoginFormValues>({ defaultValues: { email: '', password: '' } })
  const [serverError, setServerError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null)
    setSubmitting(true)
    try {
      const result = await signIn(values)
      if (!result.ok) {
        // The server's own string, not a rewritten one — see the module doc
        // comment above for why flattening this would lose information.
        // `signIn` (`api/client.ts`) now handles every failure shape itself
        // (a transport rejection, or a response whose body isn't usable
        // JSON) and always resolves rather than throwing, so this branch is
        // reached for all of them — the `catch` below is no longer where a
        // failed sign-in normally lands (web-pre-deploy item 3).
        setServerError(result.message)
        return
      }
      // `refetchType: 'all'`, NOT the default `'active'` — the bug this
      // fixes was reproduced on a real installed PWA (iPhone, 2026-08-03,
      // twice in a row: correct details, empty form back with no error, and
      // the SECOND attempt works every time).
      //
      // Nothing OBSERVES `['session','me']` while this screen is up —
      // `AppShell` is unmounted, and `LoginRoute` never reads `useSession()`.
      // Under the default `'active'`, `invalidateQueries` therefore marks the
      // query stale and refetches NOTHING, so this `await` resolves
      // immediately having done nothing at all: the contract this line was
      // written to provide ("whatever next reads that key gets a fresh
      // `GET /api/me`") was never actually delivered for the ordinary case.
      // `navigate('/')` then mounts `AppShell`, whose `useSession()` still
      // reads the pre-sign-in `null` cached at boot — `isLoading` is
      // `isPending && isFetching` (`@tanstack/query-core@5.101.4`,
      // `queryObserver.ts:561`) and `isPending` is `false` the moment ANY
      // data exists, `null` included — so the shell's first guard does not
      // hold, `!isSignedIn` reads that stale `null`, and the user is bounced
      // straight back here. The refetch the shell's own mount triggered then
      // lands and caches the real principal, which is precisely why the
      // second attempt succeeds and why this never looked like an error.
      //
      // With `'all'`, the inactive query is refetched and this `await` waits
      // for it, so by the time `navigate('/')` runs the cache holds the
      // signed-in principal and there is no stale value left to bounce on.
      // Covered by `test/auth.test.tsx`'s "lands in the app after a sign-in
      // once the boot `/api/me` has already answered 401".
      await queryClient.invalidateQueries({ queryKey: ['session', 'me'], refetchType: 'all' })
      navigate('/')
    } catch {
      // `signIn` no longer throws for a transport rejection or an unparsable
      // error body — see its own doc comment (`api/client.ts`) — so this
      // branch is reached only for something outside its scope entirely:
      // `invalidateQueries` or `navigate` above, i.e. react-query and
      // react-router internals.
      //
      // The thrown value is DISCARDED, never rendered — final-review bucket 1
      // item 3, and task 3's own recorded carry-forward. This line used to
      // read `cause instanceof Error ? cause.message : 'Sign in failed.'`,
      // which puts a library's internal exception string ("Failed to
      // execute 'pushState' on 'History'", a react-query invariant, a
      // minified name after a production build) in front of a site manager
      // on the ONE screen they need to get into the app at all. CLAUDE.md's
      // rule is "no developer identifier on a screen a non-technical user
      // depends on" — a rule about screens, not about the two functions
      // item 3 originally named. The narrowness of the remaining throwers is
      // an argument that this is RARE, never that the string would be
      // readable if it happened.
      //
      // NOT covered by a test, stated rather than papered over: reaching
      // this branch means making `invalidateQueries` or `navigate` throw,
      // and the only way to do that from a test is to mock react-query or
      // react-router internals — which `docs/frontend-testing.md` rule 4 and
      // CLAUDE.md both forbid. A test that mocked its way here would be
      // testing the mock. The fix SHORTENS the line, so the regression it
      // guards against is re-adding code, not deleting it.
      setServerError('Sign in failed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="text-xl font-semibold">Sign in</h1>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="email"
            rules={{ required: 'Email is required.' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            rules={{ required: 'Password is required.' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <Button type="submit" disabled={submitting}>
            Sign in
          </Button>
        </form>
      </Form>
    </main>
  )
}
